import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMenu, validateQR, callWaiter, requestBill, getSessionOrders, resolveSlug } from '../services/api';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useSession } from '../context/SessionContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { useCustomDomainContext } from '../context/CustomDomainContext';
import { getMenuLanguage } from '../i18n';
import { trackEvent, trackPixel, hashForAnalytics } from '../utils/analytics';
import SearchBar from '../components/SearchBar';
import FilterChips from '../components/FilterChips';
import CategoryNav from '../components/CategoryNav';
import CartDrawer from '../components/CartDrawer';
import CustomerAuthModal from '../components/CustomerAuthModal';
import FloatingActionButtons from '../components/FloatingActionButtons';
import ItemDetailModal from '../components/ItemDetailModal';
import ProductCardSkeleton from '../components/ProductCard/ProductCardSkeleton';
import LazyCategory from '../components/LazyCategory';
import PullToRefresh from '../components/PullToRefresh';
import PromosSection from '../components/PromosSection';
import TopItemsSection from '../components/TopItemsSection';
import RestaurantHeader from '../components/RestaurantHeader';
import StoriesBar from '../components/StoriesBar';
import Snackbar from '../components/Snackbar';
import WifiBanner from '../components/WifiBanner';
import StoryViewer from '../components/StoryViewer';
import ViewModeToggle from '../components/ViewModeToggle';
import useViewMode from '../hooks/useViewMode';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { storage } from '../utils/storage';
import './MenuPage.css';

function MenuPage({ mode = 'dine_in' }) {
  const { t } = useTranslation();
  const { restaurantId: paramRestaurantId, tableNumber: paramTableNumber, slug } = useParams();
  const { restaurantId: domainRestaurantId, organizationId } = useCustomDomainContext();

  // Slug resolution state
  const [slugRestaurantId, setSlugRestaurantId] = useState(null);
  const [slugLoading, setSlugLoading] = useState(!!slug);
  const [slugError, setSlugError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    if (slugRestaurantId) return;

    // Default domain has no organizationId — slug resolution requires it
    if (!organizationId) {
      setSlugError(t('menu.restaurantNotFound'));
      setSlugLoading(false);
      return;
    }

    const cacheKey = `slug:${slug}:${organizationId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setSlugRestaurantId(JSON.parse(cached).restaurantId);
      setSlugLoading(false);
      return;
    }

    setSlugLoading(true);
    resolveSlug(slug, organizationId)
      .then(data => {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
        setSlugRestaurantId(data.restaurantId);
      })
      .catch(() => setSlugError(t('menu.restaurantNotFound')))
      .finally(() => setSlugLoading(false));
  }, [slug, organizationId, slugRestaurantId, t]);

  const restaurantId = paramRestaurantId || slugRestaurantId || domainRestaurantId;
  const tableNumber = paramTableNumber || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuLanguage, setMenuLanguage] = useState(getMenuLanguage());
  const { cart, addItem, removeItem, updateQuantity, clearCart } = useCart();
  const { session, startSession, endSession } = useSession();
  const { preference, cyclePreference } = useTheme();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { darkModeEnabled } = useBranding();
  const featureFlags = useFeatureFlags();
  const navigate = useNavigate();

  // New state for enhanced UI
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [viewMode, setViewMode] = useViewMode('grid');
  const [isActionsDrawerOpen, setIsActionsDrawerOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [hasPendingBill, setHasPendingBill] = useState(false);
  const hasOrdersInSessionRef = useRef(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Track mobile breakpoint for responsive search bar
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 480px)').matches);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 480px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Snackbar state for add-to-cart confirmation
  const [snackbar, setSnackbar] = useState({ visible: false, message: '' });
  const undoInfoRef = useRef(null);

  const showAddedSnackbar = useCallback((itemName, item) => {
    undoInfoRef.current = { itemId: item.itemId, selectedModifiers: item.selectedModifiers };
    setSnackbar({ visible: true, message: t('cart.itemAdded', { name: itemName }) });
  }, [t]);

  const handleSnackbarDismiss = useCallback(() => {
    setSnackbar(prev => ({ ...prev, visible: false }));
    undoInfoRef.current = null;
  }, []);

  const handleSnackbarUndo = useCallback(() => {
    const info = undoInfoRef.current;
    if (info) {
      // Find the last cart item matching this itemId
      const match = [...cart].reverse().find(i => i.itemId === info.itemId);
      if (match) {
        if (match.quantity > 1) {
          updateQuantity(match.cartItemId, match.quantity - 1);
        } else {
          removeItem(match.cartItemId);
        }
      }
    }
    setSnackbar(prev => ({ ...prev, visible: false }));
    undoInfoRef.current = null;
  }, [cart, removeItem, updateQuantity]);

  // Per-category pagination
  const ITEMS_PER_CATEGORY = 8;
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const handleExpandCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      next.add(categoryId);
      return next;
    });
  }, []);

  // Stories state
  const [isStoryOpen, setIsStoryOpen] = useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  const [viewedStories, setViewedStories] = useState(() => {
    try {
      const saved = storage.get('viewedStories');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (!restaurantId) return;
    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, tableNumber]);

  useEffect(() => {
    const handleMenuLanguageChange = () => {
      setMenuLanguage(getMenuLanguage());
    };

    window.addEventListener('menuLanguageChange', handleMenuLanguageChange);
    return () => {
      window.removeEventListener('menuLanguageChange', handleMenuLanguageChange);
    };
  }, []);

  // Auto-open product from shared URL (?item=)
  useEffect(() => {
    const itemId = searchParams.get('item');
    if (itemId && menu.length > 0 && !loading) {
      // Find item in menu
      let foundItem = null;
      for (const category of menu) {
        foundItem = category.items?.find(item => item.itemId === itemId);
        if (foundItem) break;
      }

      if (foundItem) {
        setSelectedItem(foundItem);
      }

      // Clean up URL param
      searchParams.delete('item');
      setSearchParams(searchParams, { replace: true });
    }
  }, [menu, loading, searchParams, setSearchParams]);

  // Check for pending bill — one-time mount check + polling after orders exist
  useEffect(() => {
    if (!session?.sessionId || mode !== 'dine_in') return;

    let cancelled = false;

    const checkOrders = async () => {
      try {
        const response = await getSessionOrders(session.sessionId);
        if (cancelled) return;
        const orders = response.data || response.orders || [];

        if (orders.length > 0) {
          hasOrdersInSessionRef.current = true;
          const hasUnpaidOrders = orders.some(order =>
            (order.status === 'completed' || order.status === 'delivered') &&
            !order.paymentMethod
          );
          setHasPendingBill(hasUnpaidOrders);
        }
      } catch (error) {
        console.error('Error checking orders:', error);
      }
    };

    // Initial check on mount
    checkOrders();

    // Poll every 30s — interval callback re-checks the flag via ref
    const interval = setInterval(() => {
      if (hasOrdersInSessionRef.current) {
        checkOrders();
      }
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [session?.sessionId, mode]);

  const handleOrderPlaced = useCallback(() => {
    hasOrdersInSessionRef.current = true;
  }, []);

  const initializeSession = async () => {
    try {
      const storedSession = sessionStorage.getItem('digitalMenuSession');
      let parsed = null;
      try { parsed = storedSession ? JSON.parse(storedSession) : null; } catch { /* ignore */ }

      // Check if stored session matches current URL (same restaurant + table)
      const sessionMatchesUrl = parsed &&
        parsed.restaurantId === restaurantId &&
        String(parsed.tableNumber) === String(tableNumber);

      if (sessionMatchesUrl) {
        // Existing session for this table — reuse it, update slug if present
        if (slug && !parsed.slug) {
          parsed.slug = slug;
          sessionStorage.setItem('digitalMenuSession', JSON.stringify(parsed));
        }
        startSession(parsed);
        await loadMenu();
        return;
      }

      // Session is missing or for a different table — clear old data
      if (parsed) {
        sessionStorage.removeItem('digitalMenuSession');
        endSession();
        clearCart();
      }

      // TAKEAWAY MODE
      if (mode === 'takeaway') {
        const sessionData = {
          restaurantId,
          tableNumber: null,
          sessionId: `takeaway-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ...(slug && { slug })
        };

        sessionStorage.setItem('digitalMenuSession', JSON.stringify(sessionData));
        startSession(sessionData);
        await loadMenu();
        return;
      }

      // Token is stripped from URL in index.html before analytics load.
      // Read from window.__dmToken (set by early script) or fallback to searchParams.
      const token = window.__dmToken || searchParams.get('token');
      if (window.__dmToken) delete window.__dmToken;

      if (token) {
        // Ensure token is removed from URL (safety net if early script missed it)
        if (searchParams.has('token')) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('token');
          window.history.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
        }

        const qrCode = `QR-${restaurantId}-${tableNumber}-${token}`;
        const response = await validateQR(qrCode);

        if (response.valid) {
          const sessionData = {
            restaurantId: response.restaurantId,
            tableNumber: response.tableNumber,
            sessionId: response.sessionId,
            ...(slug && { slug })
          };

          sessionStorage.setItem('digitalMenuSession', JSON.stringify(sessionData));
          startSession(sessionData);
        } else {
          setError(t('menu.invalidQR'));
          setLoading(false);
          return;
        }
      } else {
        // Deep-link without token (e.g. bookmarked or shared URL).
        // Create a tokenless session so the menu loads normally.
        const sessionData = {
          restaurantId,
          tableNumber,
          sessionId: `dl-${restaurantId}-${tableNumber}-${Date.now()}`,
          ...(slug && { slug })
        };

        sessionStorage.setItem('digitalMenuSession', JSON.stringify(sessionData));
        startSession(sessionData);
      }

      await loadMenu();
    } catch (err) {
      setError(t('common.error'));
      setLoading(false);
    }
  };

  const loadMenu = async () => {
    try {
      const response = await getMenu(restaurantId);
      const menuData = response.menu || [];
      setMenu(menuData);
      trackEvent('menu_viewed', {
        restaurant_id: restaurantId,
        session_id: hashForAnalytics(session?.sessionId),
        table_id: session?.tableId,
        categories_count: menuData.length,
        items_count: menuData.reduce((sum, cat) => sum + (cat.items?.length || 0), 0),
      });
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    try {
      const response = await getMenu(restaurantId);
      setMenu(response.menu || []);
    } catch (err) {
      console.error('Error refreshing menu:', err);
    }
  }, [restaurantId]);

  // Helper to get translated text from menu item/category
  const getTranslatedText = useCallback((item, field) => {
    if (item.translations && item.translations[menuLanguage] && item.translations[menuLanguage][field]) {
      return item.translations[menuLanguage][field];
    }
    return item[field] || '';
  }, [menuLanguage]);

  const handleAddToCart = useCallback((item) => {
    // Always open the detail modal so customers can see full info
    setSelectedItem(item);
  }, []);

  const handleAddFromModal = useCallback((itemWithModifiers) => {
    addItem(itemWithModifiers);
    const itemName = getTranslatedText(itemWithModifiers, 'name') || itemWithModifiers.name;
    showAddedSnackbar(itemName, itemWithModifiers);
    trackEvent('item_added_to_cart', {
      restaurant_id: restaurantId,
      session_id: hashForAnalytics(session?.sessionId),
      item_id: itemWithModifiers.itemId,
      item_name: itemWithModifiers.name,
      item_price: itemWithModifiers.price,
      quantity: itemWithModifiers.quantity || 1,
      has_modifiers: !!(itemWithModifiers.selectedModifiers && itemWithModifiers.selectedModifiers.length > 0),
    });
    trackPixel('AddToCart', {
      content_name: itemWithModifiers.name,
      content_ids: [itemWithModifiers.itemId],
      content_type: 'product',
      value: itemWithModifiers.price,
      currency: 'ARS',
    });
    setSelectedItem(null);
  }, [addItem, restaurantId, session, getTranslatedText, showAddedSnackbar]);

  // Helper to check if item is available based on current day/time
  const isItemAvailable = useCallback((item) => {
    // If item doesn't have availability restrictions, it's always available
    if (!item.availability) {
      return true;
    }

    const { days, timeSlots } = item.availability;
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    if (days && days.length > 0 && !days.includes(currentDay)) {
      return false;
    }

    if (timeSlots && timeSlots.length > 0) {
      const isInTimeSlot = timeSlots.some(slot => {
        const [startHour, startMin] = slot.start.split(':').map(Number);
        const [endHour, endMin] = slot.end.split(':').map(Number);
        const startTime = startHour * 60 + startMin;
        const endTime = endHour * 60 + endMin;
        return currentTime >= startTime && currentTime <= endTime;
      });

      if (!isInTimeSlot) {
        return false;
      }
    }

    return true;
  }, []);

  // Filter menu to only include categories with available items
  const menuWithAvailableItems = useMemo(() => {
    return menu
      .map(category => ({
        ...category,
        items: category.items.filter(item => isItemAvailable(item))
      }))
      .filter(category => category.items.length > 0); // Remove empty categories
  }, [menu, isItemAvailable]);

  // Get all menu items flat for autocomplete suggestions
  const allMenuItems = useMemo(() => {
    return menuWithAvailableItems.flatMap(category =>
      category.items.map(item => ({
        ...item,
        categoryName: getTranslatedText(category, 'name')
      }))
    );
  }, [menuWithAvailableItems, getTranslatedText]);

  // Extract available filters from item tags
  const availableFilters = useMemo(() => {
    const tagCounts = {};

    menuWithAvailableItems.forEach(category => {
      category.items.forEach(item => {
        if (item.tags && Array.isArray(item.tags)) {
          item.tags.forEach(tag => {
            const normalizedTag = tag.toLowerCase().trim();
            if (normalizedTag) {
              tagCounts[normalizedTag] = (tagCounts[normalizedTag] || 0) + 1;
            }
          });
        }
      });
    });

    // Convert to array and sort by count
    return Object.entries(tagCounts)
      .map(([tag, count]) => ({
        value: tag,
        label: tag.charAt(0).toUpperCase() + tag.slice(1),
        count
      }))
      .sort((a, b) => b.count - a.count);
  }, [menuWithAvailableItems]);

  // Get all categories for CategoryNav (only those with available items)
  const categories = useMemo(() => {
    return menuWithAvailableItems.map(cat => ({
      categoryId: cat.categoryId,
      name: getTranslatedText(cat, 'name'),
      imageUrl: cat.imageUrl
    }));
  }, [menuWithAvailableItems, getTranslatedText]);

  // Filter promo items from all categories (with date/schedule validation)
  // Gated by smartMenuPromosEnabled feature flag
  const promoItems = useMemo(() => {
    if (!featureFlags.smartMenuPromosEnabled) return [];
    const allItems = menuWithAvailableItems.flatMap(category => category.items);
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    return allItems.filter(item => {
      // Always include combos and daily specials
      if (item.isCombo || item.isDailySpecial) return true;

      // For promotions, validate date range
      if (item.isPromotion) {
        if (item.promotionValidFrom && today < item.promotionValidFrom) return false;
        if (item.promotionValidTo && today > item.promotionValidTo) return false;

        // Validate happy hour schedule if present — gated by smartMenuScheduleEnabled
        if (item.promotionSchedule && item.promotionType === 'happy_hour') {
          if (!featureFlags.smartMenuScheduleEnabled) return false;
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const schedule = item.promotionSchedule;
          if (schedule.start && schedule.end) {
            const [startH, startM] = schedule.start.split(':').map(Number);
            const [endH, endM] = schedule.end.split(':').map(Number);
            if (currentMinutes < startH * 60 + startM || currentMinutes > endH * 60 + endM) {
              return false;
            }
          }
        }
        return true;
      }

      // Items with original price higher than current price
      if (item.originalPrice && item.originalPrice > item.price) return true;

      return false;
    });
  }, [menuWithAvailableItems, featureFlags.smartMenuPromosEnabled, featureFlags.smartMenuScheduleEnabled]);

  // Filter top items (popularity badges) sorted by popularityScore
  // Gated by smartMenuPopularityEnabled feature flag
  const topItems = useMemo(() => {
    if (!featureFlags.smartMenuPopularityEnabled) return [];
    const allItems = menuWithAvailableItems.flatMap(category => category.items);
    return allItems
      .filter(item => item.popularityBadge === 'top_today')
      .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0));
  }, [menuWithAvailableItems, featureFlags.smartMenuPopularityEnabled]);

  // Build a set of top item IDs to avoid duplicates in category listings
  const topItemIds = useMemo(() => {
    return new Set(topItems.map(item => item.itemId));
  }, [topItems]);

  // Prepare stories from virtual categories (Destacados, Promo) — not food categories
  const stories = useMemo(() => {
    const result = [];

    // "Destacados" — featured items across all categories
    const featuredItems = menuWithAvailableItems.flatMap(cat =>
      cat.items.filter(item => item.isFeatured)
    );
    if (featuredItems.length > 0) {
      result.push({
        id: 'featured',
        name: t('stories.featured'),
        imageUrl: featuredItems[0]?.imageUrl || null,
        itemCount: featuredItems.length,
      });
    }

    // "Promo" — promotional items (combos, daily specials, promotions)
    if (promoItems.length > 0) {
      result.push({
        id: 'daily-specials',
        name: t('stories.promo'),
        imageUrl: promoItems[0]?.imageUrl || null,
        itemCount: promoItems.length,
      });
    }

    return result;
  }, [menuWithAvailableItems, promoItems, t]);

  // Get items for a specific story
  const getItemsForStory = useCallback((storyId) => {
    if (storyId === 'daily-specials') {
      return promoItems;
    }
    if (storyId === 'featured') {
      return menuWithAvailableItems
        .flatMap(cat => cat.items.filter(item => item.isFeatured))
        .sort((a, b) => (a.storyOrder || 0) - (b.storyOrder || 0));
    }
    return [];
  }, [menuWithAvailableItems, promoItems]);

  // Story handlers
  const handleStoryClick = useCallback((storyId, index) => {
    setActiveStoryIndex(index);
    setIsStoryOpen(true);
  }, []);

  const handleStoryViewed = useCallback((storyId) => {
    setViewedStories(prev => {
      const next = new Set(prev);
      next.add(storyId);
      try {
        storage.set('viewedStories', JSON.stringify([...next]));
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  }, []);

  // Build a set of promo item IDs to avoid duplicates in category listings
  const promoItemIds = useMemo(() => {
    return new Set(promoItems.map(item => item.itemId));
  }, [promoItems]);

  // Filter items based on search, category and tags
  const filteredMenu = useMemo(() => {
    let result = menuWithAvailableItems;

    // When promos/top-items sections are visible (no search, no category, no filters),
    // exclude their items from category listings to avoid duplicates
    const noFiltersActive = !searchQuery && !activeCategory && activeFilters.length === 0;
    if (noFiltersActive) {
      const excludeIds = new Set([
        ...(promoItems.length > 0 ? promoItemIds : []),
        ...(topItems.length > 0 ? topItemIds : []),
      ]);
      if (excludeIds.size > 0) {
        result = result.map(category => ({
          ...category,
          items: category.items.filter(item => !excludeIds.has(item.itemId))
        })).filter(category => category.items.length > 0);
      }
    }

    if (activeCategory) {
      result = result.filter(cat => cat.categoryId === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.map(category => ({
        ...category,
        items: category.items.filter(item => {
          const name = String(getTranslatedText(item, 'name') || '').toLowerCase();
          const description = String(getTranslatedText(item, 'description') || '').toLowerCase();
          return name.includes(query) || description.includes(query);
        })
      })).filter(category => category.items.length > 0);
    }

    // Apply tag filters
    if (activeFilters.length > 0) {
      result = result.map(category => ({
        ...category,
        items: category.items.filter(item => {
          if (!item.tags || !Array.isArray(item.tags)) return false;
          const itemTags = item.tags.map(t => t.toLowerCase().trim());
          return activeFilters.every(filter => itemTags.includes(filter));
        })
      })).filter(category => category.items.length > 0);
    }

    return result;
  }, [menuWithAvailableItems, activeCategory, searchQuery, activeFilters, getTranslatedText, promoItems, promoItemIds, topItems, topItemIds]);

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query) {
      setActiveCategory(null);
    }
  };

  const handleSuggestionSelect = (item) => {
    setSelectedItem(item);
  };

  const handleFilterToggle = (filterValue) => {
    setActiveFilters(prev =>
      prev.includes(filterValue)
        ? prev.filter(f => f !== filterValue)
        : [...prev, filterValue]
    );
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
  };

  const handleAuthSuccess = () => {
    setIsAuthModalOpen(false);
    // After auth, try checkout again if needed, or just let user click again
  };

  const handleCallWaiter = async () => {
    await callWaiter({
      restaurantId: session?.restaurantId,
      sessionId: session?.sessionId,
      tableNumber: session?.tableNumber,
      customerName: session?.customer?.name || 'Guest',
      requestType: 'assistance'
    });
  };

  const handleRequestBill = async () => {
    try {
      await requestBill({
        restaurantId: session?.restaurantId,
        sessionId: session?.sessionId,
        tableNumber: session?.tableNumber,
        customerName: session?.customer?.name || 'Guest'
      });
      navigate('/orders?tab=bill');
    } catch (error) {
      console.error('Error requesting bill:', error);
      alert(t('floating.bill_requested_error') || 'Error al solicitar la cuenta');
    }
  };

  if (slugLoading) {
    return (
      <div className="menu-page-layout">
        <div className="main-content">
          <div className="menu-skeleton-header">
            <div className="skeleton-bar" />
          </div>
          <div className="menu-skeleton-content">
            <div className={`items-grid items-grid--${viewMode}`}>
              <ProductCardSkeleton viewMode={viewMode} count={6} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (slugError) {
    return <div className="error" role="alert">{slugError}</div>;
  }

  if (loading) {
    return (
      <div className="menu-page-layout">
        <div className="main-content">
          <div className="menu-skeleton-header">
            <div className="skeleton-bar" />
          </div>
          <div className="menu-skeleton-content">
            <div className={`items-grid items-grid--${viewMode}`}>
              <ProductCardSkeleton viewMode={viewMode} count={6} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error" role="alert">{error}</div>;
  }

  return (
    <div className={`menu-page-layout`}>
      {/* Main Content Area */}
      <div className={`main-content`}>
        <PullToRefresh onRefresh={handleRefresh} disabled={loading}>
        {/* Restaurant Branding Header */}
        <header>
          <RestaurantHeader
            tableNumber={session?.tableNumber}
            mode={mode}
          />
        </header>

        {/* Tools Bar - Search, View Mode, Theme */}
        <div className="menu-tools-bar">
          <SearchBar
            onSearch={handleSearch}
            alwaysVisible={!isMobile}
            suggestions={allMenuItems}
            onSuggestionSelect={handleSuggestionSelect}
            getTranslatedText={getTranslatedText}
          />
          <div className="menu-tools-bar__actions">
            <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
            {darkModeEnabled !== false && (
              <button
                className="theme-toggle-btn"
                onClick={cyclePreference}
                title={`${t('theme.label')}: ${t(`theme.${preference}`)}`}
                aria-label={`${t('theme.label')}: ${t(`theme.${preference}`)}`}
              >
                <span className="material-icons" aria-hidden="true">
                  {preference === 'light' ? 'light_mode' : preference === 'dark' ? 'dark_mode' : 'brightness_auto'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Stories Bar - Instagram style */}
        <StoriesBar
          stories={stories}
          onStoryClick={handleStoryClick}
          viewedStories={viewedStories}
        />

        {/* Category Filter - responsive: icons on mobile, tabs on desktop */}
        <CategoryNav
          categories={categories}
          activeCategory={activeCategory}
          onCategorySelect={handleCategorySelect}
        />

        {/* Filter Chips - dietary and tag filters */}
        {availableFilters.length > 0 && (
          <FilterChips
            availableFilters={availableFilters}
            activeFilters={activeFilters}
            onFilterToggle={handleFilterToggle}
            onClearAll={handleClearFilters}
          />
        )}

        {/* WiFi Banner - below nav/filters so menu content stays above-the-fold */}
        {mode !== 'takeaway' && <WifiBanner />}

        {/* Promos Section - only show when not searching or filtering */}
        {promoItems.length > 0 && !searchQuery && !activeCategory && activeFilters.length === 0 && (
          <PromosSection
            promoItems={promoItems}
            onAddToCart={handleAddToCart}
            onItemClick={(item) => setSelectedItem(item)}
            getTranslatedText={getTranslatedText}
            title={t('promos.title')}
          />
        )}

        {/* Top Items Section - only show when not searching or filtering */}
        {topItems.length > 0 && !searchQuery && !activeCategory && activeFilters.length === 0 && (
          <TopItemsSection
            topItems={topItems}
            onAddToCart={handleAddToCart}
            onItemClick={(item) => setSelectedItem(item)}
            getTranslatedText={getTranslatedText}
            restaurantId={restaurantId}
            tableNumber={tableNumber}
            mode={mode}
          />
        )}

        <div className="menu-container">
          {filteredMenu.length === 0 ? (
            <div className="no-results" role="status" aria-live="polite">
              <span className="material-icons" aria-hidden="true">search_off</span>
              <p>{searchQuery.trim() ? t('search.noResults') : t('filters.noResults')}</p>
              <p className="no-results-hint">
                {searchQuery.trim() ? t('search.noResultsHint') : t('filters.noResultsHint')}
              </p>
            </div>
          ) : (
            filteredMenu.map((category) => {
              const isExpanded = expandedCategories.has(category.categoryId);
              // When searching/filtering, show all results; otherwise paginate
              const isFiltered = !!(searchQuery.trim() || activeFilters.length > 0);
              const visibleItems = (isExpanded || isFiltered)
                ? category.items
                : category.items.slice(0, ITEMS_PER_CATEGORY);
              const hiddenCount = category.items.length - visibleItems.length;

              return (
                <LazyCategory
                  key={category.categoryId}
                  category={category}
                  viewMode={viewMode}
                  visibleItems={visibleItems}
                  hiddenCount={hiddenCount}
                  onExpandCategory={handleExpandCategory}
                  onAddToCart={handleAddToCart}
                  onItemClick={(item) => setSelectedItem(item)}
                  getTranslatedText={getTranslatedText}
                  isFavorite={isFavorite}
                  onToggleFavorite={toggleFavorite}
                  restaurantId={restaurantId}
                  tableNumber={tableNumber}
                  mode={mode}
                  showMoreLabel={t('menu.showMore', { count: hiddenCount })}
                  onPromoExpired={handleRefresh}
                />
              );
            })
          )}
        </div>
        </PullToRefresh>
      </div>

      {/* Cart Summary Side Panel (Desktop only) - using embedded CartDrawer */}
      <div className={`cart-sidebar${cart.length === 0 ? ' cart-sidebar--hidden' : ''}`}>
        <CartDrawer embedded={true} onClose={() => {}} mode={mode} onOrderPlaced={handleOrderPlaced} />
      </div>

      {/* Cart Drawer (Mobile overlay mode) */}
      <CartDrawer
        isOpen={isActionsDrawerOpen}
        onClose={() => setIsActionsDrawerOpen(false)}
        mode={mode}
        onOrderPlaced={handleOrderPlaced}
      />


      {/* Floating Action Buttons */}
      {mode === 'dine_in' && (
        <FloatingActionButtons
          onCallWaiter={handleCallWaiter}
          onRequestBill={handleRequestBill}
          showBillButton={hasPendingBill}
          cartVisible={cart.length > 0}
        />
      )}

      <CustomerAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
        restaurantId={restaurantId}
        organizationId={session?.organizationId}
      />

      <ItemDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={handleAddFromModal}
        getTranslatedText={getTranslatedText}
        restaurantId={restaurantId}
        tableNumber={tableNumber}
        mode={mode}
      />

      {/* Add-to-cart confirmation snackbar */}
      <Snackbar
        message={snackbar.message}
        isVisible={snackbar.visible}
        onDismiss={handleSnackbarDismiss}
        onUndo={handleSnackbarUndo}
      />

      {/* Story Viewer Modal */}
      <StoryViewer
        isOpen={isStoryOpen}
        stories={stories}
        initialStoryIndex={activeStoryIndex}
        getItemsForStory={getItemsForStory}
        onClose={() => setIsStoryOpen(false)}
        onAddToCart={handleAddToCart}
        getTranslatedText={getTranslatedText}
        onStoryViewed={handleStoryViewed}
        restaurantId={restaurantId}
        tableNumber={tableNumber}
        mode={mode}
      />
    </div>
  );
}

export default MenuPage;
